import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Field } from '@/components/ui';
import {
  useResetStaffMfa,
  type ResetStaffMfaResult,
} from '@/features/admin-v2/api';
import { isApiError, type ApiError } from '@/lib/api';
import styles from './ResetStaffMfaDialog.module.css';

function errorKey(error: ApiError) {
  switch (error.code) {
    case 'SUPER_ADMIN_ONLY':
      return 'staff.resetErrors.superAdminOnly';
    case 'MFA_SELF_RESET_FORBIDDEN':
      return 'staff.resetErrors.selfReset';
    case 'STAFF_NOT_FOUND':
      return 'staff.resetErrors.notFound';
    case 'VALIDATION_FAILED':
      return 'staff.resetErrors.invalidReason';
    default:
      return 'staff.resetErrors.generic';
  }
}

export function ResetStaffMfaDialog({
  staffId,
  staffName,
  onClose,
}: {
  staffId: string;
  staffName: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const reset = useResetStaffMfa();
  const [reason, setReason] = useState('');
  const [result, setResult] = useState<ResetStaffMfaResult | null>(null);
  const trimmedReason = reason.trim();
  const error = reset.error;

  async function confirm() {
    try {
      const response = await reset.mutateAsync({
        staffId,
        reason: trimmedReason,
      });
      setReason('');
      setResult(response);
    } catch {
      // The mutation retains the ApiError for the code-specific inline message.
    }
  }

  return (
    <Dialog
      open
      variant="modal"
      title={t('staff.resetTwoFactorTitle', { name: staffName })}
      onClose={onClose}
      dismissible={!reset.isPending}
      footer={
        result ? (
          <Button variant="primary" onClick={onClose}>
            {t('common.close')}
          </Button>
        ) : (
          <>
            <Button disabled={reset.isPending} onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              disabled={!trimmedReason || reason.length > 500}
              loading={reset.isPending}
              onClick={() => void confirm()}
            >
              {t('staff.confirmResetTwoFactor')}
            </Button>
          </>
        )
      }
    >
      <div className={styles.body}>
        {result ? (
          <>
            <p className={styles.success} role="status">
              <strong>{t('staff.resetComplete')}</strong>
            </p>
            <p>
              {t('staff.resetCompleteBody', {
                sessions: result.sessionsRevoked,
                codes: result.recoveryCodesRevoked,
              })}
            </p>
          </>
        ) : (
          <>
            <p className={styles.warning}>{t('staff.resetTwoFactorBody')}</p>
            <Field
              label={t('staff.resetReason')}
              hint={t('staff.resetReasonHint')}
              error={
                reason.length > 500
                  ? t('staff.resetErrors.invalidReason')
                  : undefined
              }
            >
              <textarea
                className={styles.textarea}
                value={reason}
                maxLength={501}
                placeholder={t('staff.resetReasonPlaceholder')}
                onChange={(event) => setReason(event.target.value)}
              />
            </Field>
            {error && (
              <p className={styles.error} role="alert">
                {isApiError(error)
                  ? t(errorKey(error))
                  : t('staff.resetErrors.generic')}
                {isApiError(error) && error.requestId
                  ? ` ${t('common.requestId', { id: error.requestId })}`
                  : ''}
              </p>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}
